import { useEffect, useRef, useState } from 'react';
import { Button, RadioGroup, ConfirmDialog, Dialog, Input, SelectField } from '@/design-system';
import { Columns3, Layers3 } from 'lucide-react';
import { PackageComparisonDialog } from './PackageComparisonDialog';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/core/data-fetching/queryKeys';
import { planLicensingService, type PlanTier } from '../api/plan-licensing.service';
import { useModulePackages } from '@/features/modules';
import { PricesEditor, type DraftPrice } from '@/design-system/billing';
import { plansService } from '../api/plans.service';
import type { Plan, PlanFormState, PlanInterval } from '../types';
import { emptyPlanForm } from '../utils/plan-build';
import type { PlanEditorMutations } from '../hooks/usePlanMutations';

type PlanEditorDialogProps = {
  isOpen: boolean;
  mode: 'create' | 'edit';
  plan: Plan | null;
  onClose: () => void;
  mutations: PlanEditorMutations;
};

export function PlanEditorDialog({ isOpen, mode, plan, onClose, mutations }: PlanEditorDialogProps) {
  const { createPlan, updatePlan } = mutations;
  const [isActive, setIsActive] = useState(true);
  const [formData, setFormData] = useState<PlanFormState>(emptyPlanForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [priceDrafts, setPriceDrafts] = useState<DraftPrice[]>([]);
  const [licensing, setLicensing] = useState<{ packageId?: string; expectedPackageVersion?: number; tier: PlanTier; capabilityKeys: string[]; expectedVersion: number; isCustom: boolean; isPublic: boolean }>({ tier: 'lite', capabilityKeys: [], expectedVersion: 0, isCustom: false, isPublic: true });
  const packages = useModulePackages();
  const [confirmImpact, setConfirmImpact] = useState(false);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const licensingDraftKey = useRef<string | null>(null);
  const formDraftKey = useRef<string | null>(null);
  const catalogQuery = useQuery({ queryKey: queryKeys.plans.capabilities, queryFn: planLicensingService.catalog, enabled: isOpen });
  const configurationQuery = useQuery({ queryKey: queryKeys.plans.configuration(plan?.id ?? ''), queryFn: () => planLicensingService.configuration(plan!.id), enabled: isOpen && mode === 'edit' && !!plan });

  useEffect(() => {
    if (!isOpen) { licensingDraftKey.current = null; setComparisonOpen(false); return; }
    const key = `${mode}:${plan?.id ?? 'new'}`;
    if (licensingDraftKey.current === key || !catalogQuery.data || !packages.data || (mode === 'edit' && !configurationQuery.data)) return;
    const selected = mode === 'create' ? packages.data.find((item) => item.tier === 'lite') : packages.data.find((item) => item.id === configurationQuery.data?.packageId);
    if (mode === 'create') setLicensing({ packageId: selected?.id, expectedPackageVersion: selected?.version, tier: selected?.tier ?? 'lite', capabilityKeys: selected?.capabilityKeys ?? [], expectedVersion: 0, isCustom: false, isPublic: true });
    else if (configurationQuery.data) setLicensing({ packageId: selected?.id, expectedPackageVersion: selected?.version, tier: configurationQuery.data.tier ?? 'lite', capabilityKeys: configurationQuery.data.capabilityKeys, expectedVersion: configurationQuery.data.version, isCustom: configurationQuery.data.isCustom ?? false, isPublic: configurationQuery.data.isPublic ?? true });
    licensingDraftKey.current = key;
  }, [isOpen, mode, plan?.id, catalogQuery.data, configurationQuery.data, packages.data]);

  useEffect(() => {
    if (!isOpen) { formDraftKey.current = null; return; }
    const key = `${mode}:${plan?.id ?? 'new'}`;
    if (formDraftKey.current === key || (mode === 'edit' && !plan)) return;
    if (mode === 'create') {
      setFormData(emptyPlanForm());
      setIsActive(true);
      setPriceDrafts([]);
      setFormErrors({});
    } else if (plan) {
      setIsActive(plan.isActive);
      const limits = plan.limits ?? {};
      setFormData({
        name: plan.name,
        description: plan.description || '',
        interval: (plan.interval as PlanInterval) || 'month',
        maxUsers: limits.users?.toString() ?? '',
        maxWhatsappConnections: limits.whatsapp_connections?.toString() ?? '1',
        storageMB: limits.storage_mb?.toString() ?? '',
      });
      setFormErrors({});
    }
    formDraftKey.current = key;
  }, [isOpen, mode, plan?.id, plan]);

  const validateForm = (opts: { requirePrices: boolean }) => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Nome é obrigatório';
    else if (formData.name.length < 2) errors.name = 'Nome deve ter no mínimo 2 caracteres';
    const included = Number(formData.maxWhatsappConnections);
    if (!formData.maxWhatsappConnections.trim() || !Number.isInteger(included) || included < 0 || included > 100000) errors.maxWhatsappConnections = 'Informe entre 0 e 100.000 canais incluídos.';
    if (opts.requirePrices) {
      if (priceDrafts.length === 0) {
        errors.prices = 'Cadastre ao menos um preço por moeda.';
      } else if (!priceDrafts.some((d) => d.isDefault)) {
        errors.prices = 'Marque uma moeda como padrão.';
      } else if (priceDrafts.some((d) => !d.unitAmount || d.unitAmount <= 0)) {
        errors.prices = 'Todos os preços devem ser maiores que zero.';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (mode === 'create') {
      if (!validateForm({ requirePrices: true })) return;
      try {
        await createPlan.mutateAsync({ form: formData, priceDrafts, licensing, isActive });
        onClose();
      } catch {
        /* toast handled in mutation */
      }
      return;
    }
    if (!plan) return;
    if (!validateForm({ requirePrices: false })) return;
    try {
      await updatePlan.mutateAsync({ planId: plan.id, form: formData, licensing, isActive });
      onClose();
    } catch {
      /* toast handled in mutation */
    }
  };

  const isSubmitting = createPlan.isPending || updatePlan.isPending;
  const configurationReady = !!catalogQuery.data && !!licensing.packageId && !!packages.data && (mode === 'create' || !!configurationQuery.data);

  const requestSubmit = () => {
    if (mode === 'edit' && (configurationQuery.data?.affectedCompanies ?? 0) > 0) setConfirmImpact(true);
    else void handleSubmit();
  };

  return (
    <><Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Novo Plano' : 'Editar Plano'}
      icon={<Layers3 />}
      description="Defina os módulos incluídos, os preços por licença e os limites do plano."
      size="lg"
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>Cancelar</Button>
        <Button onClick={requestSubmit} isLoading={isSubmitting} disabled={!configurationReady}>{mode === 'create' ? 'Criar' : 'Salvar'}</Button>
      </>}
    >
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Dados básicos</h3>
          <div className="space-y-3">
            <Input
              label="Nome"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={formErrors.name}
              required
            />
            <Input
              label="Descrição"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Opcional"
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <SelectField label="Recorrência"
                value={formData.interval}
                onChange={(value) => setFormData({ ...formData, interval: value as PlanInterval })}
                options={[{ value: 'month', label: 'Mensal' }, { value: 'semester', label: 'Semestral' }, { value: 'year', label: 'Anual' }]} />
              <SelectField label="Visibilidade" value={licensing.isPublic ? 'public' : 'internal'} disabled={isSubmitting} onChange={(value) => setLicensing({ ...licensing, isPublic: value === 'public' })} options={[{ value: 'public', label: 'Público' }, { value: 'internal', label: 'Interno' }]} />
              <SelectField label="Status" value={isActive ? 'active' : 'inactive'} disabled={isSubmitting} onChange={(value) => setIsActive(value === 'active')} options={[{ value: 'active', label: 'Ativo' }, { value: 'inactive', label: 'Inativo' }]} />
            </div>

          </div>
        </div>

        <section className="space-y-3">
          <div>
            <RadioGroup label="Módulos incluídos" value={licensing.packageId ?? ''} disabled={isSubmitting || packages.isPending} options={packages.data?.map((item) => ({ value: item.id, label: `${item.name.replace(/\s*[·—-]\s*pacote\s*$/i, '')} ${item.capabilityKeys.length} ${item.capabilityKeys.length === 1 ? 'Módulo' : 'Módulos'}` })) ?? []} onChange={(value) => {
              const item = packages.data?.find((entry) => entry.id === value);
              if (item) setLicensing({ ...licensing, packageId: item.id, expectedPackageVersion: item.version, tier: item.tier, capabilityKeys: item.capabilityKeys });
            }} />
            {!packages.isPending && !packages.data?.length && <p className="text-sm text-gray-500">Nenhum pacote cadastrado.</p>}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            {(catalogQuery.isError || packages.isError || configurationQuery.isError) && <p role="alert">Não foi possível carregar os pacotes.</p>}
            <Button variant="subtle" size="sm" className="ml-auto" disabled={!catalogQuery.data || !packages.data?.length} onClick={() => setComparisonOpen(true)}><Columns3 aria-hidden="true" />Ver comparativo dos pacotes</Button>
          </div>
        </section>

        <div>
          {mode === 'create' ? (
            <PricesEditor mode="local" heading="Preço por licença" value={priceDrafts} onChange={setPriceDrafts} />
          ) : plan ? (
            <PricesEditor
              heading="Preço por licença"
              mode="remote"
              listPrices={() => plansService.listPrices(plan.id)}
              upsertPrice={(body) => plansService.upsertPrice(plan.id, body)}
              deletePrice={(priceId) => plansService.deletePrice(plan.id, priceId)}
            />
          ) : null}
          {formErrors.prices && <p className="mt-2 text-sm text-red-600">{formErrors.prices}</p>}
          <p className="mt-2 text-xs text-gray-500">Cada canal além da quantidade incluída custa o mesmo que uma licença do plano.</p>
          {mode === 'create' && (
            <p className="mt-2 text-xs text-gray-500">
              Cadastre o preço de uma licença por moeda. O total é a quantidade contratada × este preço, mais canais adicionais quando contratadas. A cobrança é controlada manualmente.
            </p>
          )}
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Limites do plano</h3>
          <p className="text-xs text-gray-500 mb-3">As licenças simultâneas e os canais adicionais são contratados por empresa na aba Planos. Estes limites não são multiplicados pela quantidade de licenças.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="Canais incluídos"
              type="number"
              min={0}
              max={100000}
              value={formData.maxWhatsappConnections}
              disabled={isSubmitting}
              onChange={(e) => setFormData({ ...formData, maxWhatsappConnections: e.target.value })}
              error={formErrors.maxWhatsappConnections}
            />
            <Input
              label="Armazenamento incluído (MB)"
              type="number"
              min={1}
              value={formData.storageMB}
              onChange={(e) => setFormData({ ...formData, storageMB: e.target.value })}
              placeholder="Padrão do sistema"
            />
          </div>
        </div>

        <ConfirmDialog isOpen={confirmImpact} onClose={() => setConfirmImpact(false)} onConfirm={() => { setConfirmImpact(false); void handleSubmit(); }} title="Alterar plano em uso" message={`Esta configuração será aplicada a ${configurationQuery.data?.affectedCompanies ?? 0} empresa(s) vinculada(s). Recursos removidos deixarão de permitir novas operações. Os dados e o preço contratado serão preservados.`} confirmText="Salvar para todas as empresas" />
      </div>
    </Dialog>
    {isOpen && comparisonOpen && catalogQuery.data && packages.data && <PackageComparisonDialog isOpen onClose={() => setComparisonOpen(false)} packages={packages.data} catalog={catalogQuery.data} />}
    </>
  );
}
